/**
 * AutomationOutsourceView - Displays automation recommendations and outsource opportunities
 * Reads from sentimentReport.analysis.automation_recommendations + outsource_opportunities
 * or orgReport.analysis.org_automation_roadmap + org_outsource_opportunities (org level)
 */
import React from 'react';
import {
    Zap, Users, Settings, Clock, TrendingUp, AlertTriangle,
    CheckCircle2, ArrowRight, Loader2, Brain, Briefcase,
    Target, Star, ChevronRight, RefreshCw
} from 'lucide-react';

function AutomationOutsourceView({ sentimentReport, orgReport, isOrgLevel = false, loading, onRegenerate }) {
    if (loading) {
        return (
            <div className="rp-loading">
                <Loader2 className="rp-spinner-icon" size={24} />
                <p>Loading automation & outsource analysis...</p>
            </div>
        );
    }

    const automation = isOrgLevel
        ? orgReport?.analysis?.org_automation_roadmap
        : sentimentReport?.analysis?.automation_recommendations;

    const outsource = isOrgLevel
        ? orgReport?.analysis?.org_outsource_opportunities
        : sentimentReport?.analysis?.outsource_opportunities;

    // Check if report exists but is missing new fields (stale data)
    const hasReport = isOrgLevel ? !!orgReport?.analysis : !!sentimentReport?.analysis;

    if (!automation && !outsource) {
        return (
            <div className="rp-empty-state">
                <div className="rp-empty-icon"><Zap size={32} /></div>
                <h3>{hasReport ? 'Report Needs Regeneration' : 'No Automation & Outsource Analysis Available'}</h3>
                <p>{hasReport 
                    ? 'This report was generated before automation & outsource analysis was available. Regenerate to unlock this feature.'
                    : `Generate a ${isOrgLevel ? 'organization' : 'department sentiment'} report first.`}
                </p>
                {onRegenerate && (
                    <button className="rp-btn rp-btn-primary" onClick={onRegenerate} style={{ marginTop: '1rem' }}>
                        <RefreshCw size={14} /> {hasReport ? 'Regenerate Report' : 'Generate Report'}
                    </button>
                )}
            </div>
        );
    }

    return (
        <div className="automation-outsource-view">
            {/* Summary Stats */}
            {automation && (
                <div className="ds-stat-row" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-stat-card">
                        <div className="ds-stat-value">{automation.total_automatable_hours_estimate || automation.total_estimated_automatable_hours || '—'}</div>
                        <div className="ds-stat-label">Automatable Hours/Week</div>
                    </div>
                    <div className="ds-stat-card">
                        <div className="ds-stat-value" style={{ textTransform: 'capitalize' }}>{automation.automation_maturity || automation.org_automation_maturity || '—'}</div>
                        <div className="ds-stat-label">Automation Maturity</div>
                    </div>
                    <div className="ds-stat-card">
                        <div className="ds-stat-value">{(automation.quick_automations?.length || 0) + (automation.medium_investment_automations?.length || 0) + (automation.cross_department_automation_priorities?.length || 0)}</div>
                        <div className="ds-stat-label">Opportunities Identified</div>
                    </div>
                </div>
            )}

            {/* AUTOMATION SECTION */}
            {automation && (
                <div className="ds-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="ds-section-header">
                        <h2><Zap size={18} style={{ marginRight: 8 }} /> Automation Recommendations</h2>
                        <p className="ds-section-subtitle">
                            {isOrgLevel 
                                ? (automation.org_automation_summary || 'Cross-department automation opportunities')
                                : 'Tasks that can be automated to free up team capacity'}
                        </p>
                    </div>
                    <div className="ds-section-body">
                        {/* Quick Automations */}
                        {(automation.quick_automations || []).length > 0 && (
                            <div className="auto-category">
                                <h3 className="auto-category-title">
                                    <Zap size={16} style={{ color: '#34C759' }} /> Quick Wins
                                </h3>
                                <div className="auto-cards">
                                    {automation.quick_automations.map((item, i) => (
                                        <div key={i} className="auto-card auto-card-quick">
                                            <div className="auto-card-header">
                                                <h4>{item.task}</h4>
                                                <span className="auto-savings">{item.estimated_time_savings}</span>
                                            </div>
                                            <p className="auto-card-detail"><strong>Current:</strong> {item.current_process}</p>
                                            <p className="auto-card-detail"><strong>Approach:</strong> {item.automation_approach}</p>
                                            {item.tools_suggested && (
                                                <p className="auto-card-detail"><strong>Tools:</strong> {item.tools_suggested}</p>
                                            )}
                                            <div className="auto-card-meta">
                                                <span className="auto-effort auto-effort-low">{item.implementation_effort} effort</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Medium Investment */}
                        {(automation.medium_investment_automations || []).length > 0 && (
                            <div className="auto-category">
                                <h3 className="auto-category-title">
                                    <Settings size={16} style={{ color: '#FF9500' }} /> Medium Investment
                                </h3>
                                <div className="auto-cards">
                                    {automation.medium_investment_automations.map((item, i) => (
                                        <div key={i} className="auto-card auto-card-medium">
                                            <div className="auto-card-header">
                                                <h4>{item.task}</h4>
                                                <span className="auto-savings">{item.estimated_time_savings}</span>
                                            </div>
                                            <p className="auto-card-detail"><strong>Current:</strong> {item.current_process}</p>
                                            <p className="auto-card-detail"><strong>Approach:</strong> {item.automation_approach}</p>
                                            {item.estimated_timeline && (
                                                <p className="auto-card-detail"><strong>Timeline:</strong> {item.estimated_timeline}</p>
                                            )}
                                            <div className="auto-card-meta">
                                                <span className="auto-effort auto-effort-medium">{item.implementation_effort} effort</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* AI Agent Opportunities */}
                        {(automation.ai_agent_opportunities || automation.ai_transformation_opportunities || []).length > 0 && (
                            <div className="auto-category">
                                <h3 className="auto-category-title">
                                    <Brain size={16} style={{ color: '#5856D6' }} /> AI-Powered Opportunities
                                </h3>
                                <div className="auto-cards">
                                    {(automation.ai_agent_opportunities || automation.ai_transformation_opportunities || []).map((item, i) => (
                                        <div key={i} className="auto-card auto-card-ai">
                                            <div className="auto-card-header">
                                                <h4>{item.task || item.opportunity}</h4>
                                                <span className="auto-readiness" data-readiness={item.readiness}>{item.readiness}</span>
                                            </div>
                                            <p className="auto-card-detail"><strong>Current:</strong> {item.current_process || item.current_state}</p>
                                            <p className="auto-card-detail"><strong>AI Approach:</strong> {item.ai_approach || item.ai_vision}</p>
                                            <p className="auto-card-detail"><strong>Impact:</strong> {item.estimated_impact || item.expected_benefit || '—'}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Cross-department priorities (org level) */}
                        {isOrgLevel && (automation.cross_department_automation_priorities || []).length > 0 && (
                            <div className="auto-category">
                                <h3 className="auto-category-title">
                                    <Target size={16} style={{ color: '#007AFF' }} /> Cross-Department Priorities
                                </h3>
                                <div className="auto-cards">
                                    {automation.cross_department_automation_priorities.map((item, i) => (
                                        <div key={i} className="auto-card">
                                            <div className="auto-card-header">
                                                <h4>{item.automation_opportunity}</h4>
                                                <span className="auto-savings">{item.combined_time_savings}</span>
                                            </div>
                                            <p className="auto-card-detail"><strong>Departments:</strong> {(item.departments_benefited || []).join(', ')}</p>
                                            <p className="auto-card-detail"><strong>Approach:</strong> {item.approach}</p>
                                            <p className="auto-card-detail"><strong>Timeline:</strong> {item.recommended_timeline}</p>
                                            <div className="auto-card-meta">
                                                <span className="auto-effort">{item.implementation_complexity} complexity</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Freed capacity vision */}
                        {(automation.freed_capacity_vision || automation.freed_capacity_org_vision) && (
                            <div className="auto-vision-box">
                                <Star size={16} style={{ color: '#FF9500' }} />
                                <div>
                                    <h4>Freed Capacity Vision</h4>
                                    <p>{automation.freed_capacity_vision || automation.freed_capacity_org_vision}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* OUTSOURCE SECTION */}
            {outsource && (
                <div className="ds-section">
                    <div className="ds-section-header">
                        <h2><Briefcase size={18} style={{ marginRight: 8 }} /> Outsource Opportunities</h2>
                        <p className="ds-section-subtitle">
                            {isOrgLevel
                                ? (outsource.org_outsource_summary || 'Tasks that could be outsourced to free team capacity')
                                : (outsource.strategic_summary || 'Tasks suitable for outsourcing to free up higher-value work')}
                        </p>
                    </div>
                    <div className="ds-section-body">
                        {/* Outsource candidates */}
                        {(outsource.tasks_suitable_for_outsourcing || outsource.cross_department_outsource_candidates || []).length > 0 && (
                            <div className="auto-category">
                                <h3 className="auto-category-title">
                                    <ArrowRight size={16} style={{ color: '#007AFF' }} /> {isOrgLevel ? 'Cross-Department Candidates' : 'Suitable for Outsourcing'}
                                </h3>
                                <div className="auto-cards">
                                    {(outsource.tasks_suitable_for_outsourcing || outsource.cross_department_outsource_candidates || []).map((item, i) => (
                                        <div key={i} className="auto-card">
                                            <div className="auto-card-header">
                                                <h4>{item.task || item.task_category}</h4>
                                                <span className="auto-feasibility" data-level={item.outsource_feasibility}>
                                                    {item.outsource_feasibility} feasibility
                                                </span>
                                            </div>
                                            <p className="auto-card-detail"><strong>Time Impact:</strong> {item.current_time_impact || item.combined_time_impact}</p>
                                            <p className="auto-card-detail"><strong>Rationale:</strong> {item.rationale}</p>
                                            {item.risk_if_outsourced && (
                                                <p className="auto-card-detail"><strong>Risk:</strong> {item.risk_if_outsourced}</p>
                                            )}
                                            {item.alternative && (
                                                <p className="auto-card-detail auto-alternative">
                                                    <Zap size={12} /> <strong>Alternative:</strong> {item.alternative}
                                                </p>
                                            )}
                                            {isOrgLevel && item.departments_affected && (
                                                <p className="auto-card-detail"><strong>Departments:</strong> {item.departments_affected.join(', ')}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Tasks requiring internal ownership */}
                        {(outsource.tasks_requiring_internal_ownership || []).length > 0 && (
                            <div className="auto-category">
                                <h3 className="auto-category-title">
                                    <CheckCircle2 size={16} style={{ color: '#34C759' }} /> Must Stay Internal
                                </h3>
                                <div className="auto-cards">
                                    {outsource.tasks_requiring_internal_ownership.map((item, i) => (
                                        <div key={i} className="auto-card auto-card-internal">
                                            <h4>{item.task}</h4>
                                            <p className="auto-card-detail">{item.reason}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Shared services (org level) */}
                        {isOrgLevel && outsource.shared_services_opportunities && (
                            <div className="auto-vision-box">
                                <Users size={16} style={{ color: '#5856D6' }} />
                                <div>
                                    <h4>Shared Services Opportunity</h4>
                                    <p>{outsource.shared_services_opportunities}</p>
                                </div>
                            </div>
                        )}

                        {/* Freed capacity redirect */}
                        {(outsource.freed_capacity_redirect || outsource.strategic_recommendation) && (
                            <div className="auto-vision-box">
                                <TrendingUp size={16} style={{ color: '#34C759' }} />
                                <div>
                                    <h4>{isOrgLevel ? 'Strategic Recommendation' : 'Redirected Capacity'}</h4>
                                    <p>{outsource.freed_capacity_redirect || outsource.strategic_recommendation}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AutomationOutsourceView;
